<template>
  <CommunityDetailsContainer :community="community">
    <div v-show="showOverviewTab">
      <v-tabs ref="communityTabsNavigation" v-model="currentTab" show-arrows>
        <v-tab
          v-for="tab in tabs"
          :key="tab.key"
          :href="`#${tab.key}`"
          :aria-controls="`panel-${tab.key}`"
          :tabindex="currentTab !== tab.key ? -1 : 0"
        >
          <span>{{ tab.title }}</span>
          <div
            v-if="tab.itemCount"
            class="softened rounded-16 min-width-32 height-24 py-1 ml-2"
          >
            {{ tab.itemCount }}
            <span class="d-sr-only">{{ tab.itemCountAria }}</span>
          </div>
        </v-tab>
      </v-tabs>
    </div>

    <v-tabs-items v-model="currentTab" :class="!showOverviewTab && 'mt-4'">
      <CommunityOverviewTab
        id="panel-overview"
        :community="community"
        :latest-content="communityLatestContent"
        :latest-collections="communityLatestCollections.results"
      />
      <CommunityContentTab
        v-if="showContentTab"
        id="panel-content"
        :community="community"
        :community-content="communityContent"
        :question="communityContentFilter.question"
        :content-type="communityContentFilter.contentType"
        :search-term="communityContentFilter.searchTerm"
        :collection="communityContentFilter.collection"
        :content-types="contentTypes"
        :collections="allCollections"
        :announce-results-updated-text="announceResultsUpdatedText"
        @question-selected="questionSelected"
        @content-type-selected="contentTypeSelected"
        @collection-selected="collectionSelected"
        @search="onSearch"
      />
      <CommunityDiscussionTab id="panel-discussion" :community="community" />
      <ResearchDirectionsQuestionsTab
        v-if="showResearchDirectionsTab"
        id="panel-research-questions"
        :community-name="community.name"
      />
      <CommunityCollectionsTab
        id="panel-collections"
        :collections="collections"
        :collections-count="communityCollections.totalCount"
        :collections-limit="collectionsLimit"
        :community="community"
      />
    </v-tabs-items>
  </CommunityDetailsContainer>
</template>

<script>
import { mapState } from "vuex";
import mapFlags from "~/plugins/utilities/mapFlags";
import { displayIsMobile } from "~/components/utilities/displayBreakpoints";

import buildActionDispatcher from "~/actions/utilities/buildActionDispatcher";

import CommunityDetailsContainer from "~/components/communities/CommunityDetailsContainer.vue";

import CommunityOverviewTab from "~/components/communities/CommunityOverviewTab.vue";
import CommunityContentTab from "~/components/communities/CommunityContentTab.vue";
import CommunityDiscussionTab from "~/components/communities/CommunityDiscussionTab.vue";
import ResearchDirectionsQuestionsTab from "~/components/research-directions/ResearchDirectionsQuestionsTab.vue";
import CommunityCollectionsTab from "~/components/communities/CommunityCollectionsTab.vue";

import getPageTitle from "~/plugins/utilities/getPageTitle.js";
import getSiteName from "~/plugins/utilities/getSiteName.js";
import toCanonicalUrl from "~/plugins/utilities/toCanonicalUrl.js";

const COLLECTIONS_DESKTOP_LIMIT = 9;
const COLLECTIONS_MOBILE_LIMIT = 4;
const LATEST_COLLECTIONS_LIMIT = 3;
const removedCommunities = ["63c8f66ac8362cf2f1691021"];
export default {
  components: {
    CommunityDetailsContainer,
    CommunityOverviewTab,
    CommunityContentTab,
    CommunityDiscussionTab,
    ResearchDirectionsQuestionsTab,
    CommunityCollectionsTab,
  },
  middleware: [
    "ensureUserLoaded",
    ({ error, $unleash }) => {
      if (!$unleash.isEnabled("quickActionShowCommunities")) {
        return error({ statusCode: 404 });
      }
    },
  ],
  data() {
    return {
      hasLoadedContent: false,
      announceResultsUpdatedText: null,
    };
  },
  async fetch(context) {
    const { redirect, $config, params } = context;
    const partner = $config.partner;

    if (removedCommunities.includes(params.id)) {
      redirect(`/engage/${partner}/public-dashboard`);
    }

    context.params = {
      ...params,
      collectionLimit: COLLECTIONS_DESKTOP_LIMIT,
      latestCollectionLimit: LATEST_COLLECTIONS_LIMIT,
    };
    const data = await buildActionDispatcher("loadCommunityDetails")(context);
    const selectedCommunity = data ? data.selectedCommunity : null;

    if (!selectedCommunity) {
      redirect(`/engage/${partner}/community-details/not-found/${params.id}`);
    }
  },
  head() {
    const canonicalUrl = toCanonicalUrl(
      `community-details/${this.community.id}`,
      this.partner
    );
    const largeThumbnail = this.community.bannerAsset.largeThumbnail;
    const largeThumbnailUrl = largeThumbnail?.url ? largeThumbnail.url : "";
    const meta = [
      {
        name: "description",
        hid: "description",
        content: this.community.description,
      },
      // Open Graph (Facebook SEO)
      { property: "og:title", content: this.community.name },
      { property: "og:url", content: canonicalUrl },
      { property: "og:site_name", content: this.siteName },
      { property: "og:description", content: this.community.description },
      { property: "og:image", content: largeThumbnailUrl },
    ];

    return {
      title: `${this.community.name} | Communities | ${getPageTitle(
        this.siteName
      )}`,
      link: [{ rel: "canonical", href: canonicalUrl }],
      meta,
    };
  },
  computed: {
    displayIsMobile,
    ...mapState({
      siteName: (state) => getSiteName(state.theme),
      communityCollections: (state) => state.selectedCommunity.collections,
      communityLatestCollections: (state) =>
        state.selectedCommunity.latestCollections,
      community: (state) => state.selectedCommunity,
      communityLatestContent: (state) =>
        state.currentOrigin.latestCommunityContent,
      communityContent: (state) => state.currentOrigin.communityContent,
      communityContentTotalCount: (state) =>
        state.currentOrigin.communityContentTotalCount,
      researchDirectionsQuestions: (state) => state.selectedCommunity.questions,
      communityContentFilter: (state) =>
        state.communityDetails.communityContentFilter,
      contentTypes: (state) => state.referenceData.contentTypes,
      allCollections: (state) => state.referenceData.collections,
    }),
    ...mapFlags(["showCommunitiesDiscussionTab", "showCollections"]),
    partner() {
      return this.$config.partner;
    },
    collections() {
      return this.communityCollections.results.map((collection) => {
        return {
          ...collection,
          exploreLink: `/engage/${this.partner}/community-details/${collection.id}`,
        };
      });
    },
    collectionsLimit() {
      return this.displayIsMobile
        ? COLLECTIONS_MOBILE_LIMIT
        : COLLECTIONS_DESKTOP_LIMIT;
    },
    currentTab: {
      set(tab) {
        this.announceResultsUpdatedText = null;
        this.$router.replace({ query: { ...this.$route.query, show: tab } });
      },
      get() {
        return this.$route.query.show;
      },
    },
    showResearchDirectionsTab() {
      return !!(
        this.community.journalId && this.researchDirectionsQuestions.length
      );
    },
    showContentTab() {
      return (
        this.showResearchDirectionsTab ||
        (!this.community.journalId && !!this.communityContentTotalCount)
      );
    },
    showOverviewTab() {
      return !(!this.showResearchDirectionsTab && !this.showContentTab);
    },
    showCollectionTab() {
      return this.showCollections && !!this.communityCollections.totalCount;
    },
    tabs() {
      return [
        {
          show: true,
          title: "Overview",
          key: "overview",
        },
        {
          show: this.showResearchDirectionsTab,
          title: "Research Questions",
          key: "research-questions",
          itemCount: this.researchDirectionsQuestions.length,
          itemCountAria: "questions",
        },
        {
          show: this.showContentTab,
          title: "Content",
          key: "content",
          itemCount: this.communityContentTotalCount,
          itemCountAria: "articles",
        },
        {
          show: this.showCommunitiesDiscussionTab,
          title: "Discussion",
          key: "discussion",
          itemCount: this.community.latestDiscussions.totalCount,
        },
        {
          show: this.showCollectionTab,
          title: "Collections",
          key: "collections",
          itemCount: this.communityCollections.totalCount,
        },
      ].filter((tab) => tab.show === true);
    },
  },
  watch: {
    currentTab: {
      immediate: true,
      handler(tab) {
        if (tab === "content" && !this.hasLoadedContent) {
          this.loadContent();
        }
      },
    },
  },
  mounted() {
    const ARROW_LEFT = 37;
    const ARROW_UP = 38;
    const ARROW_RIGHT = 39;
    const ARROW_DOWN = 40;

    const tabs =
      this.$refs.communityTabsNavigation.$el.querySelectorAll('[role="tab"]');

    tabs.forEach((tab, i) => {
      tab.onkeydown = (e) => {
        const eventCode = e.keyCode;
        if (eventCode === ARROW_LEFT || eventCode === ARROW_UP) {
          tabs[i - 1] ? tabs[i - 1].focus() : tabs[tabs.length - 1].focus();
        } else if (eventCode === ARROW_RIGHT || eventCode === ARROW_DOWN) {
          tabs[i + 1] ? tabs[i + 1].focus() : tabs[0].focus();
        }
      };
    });
  },
  methods: {
    questionSelected(question) {
      this.$store.dispatch("selectCommunityContentFilter", { question });
      this.loadContent();
    },
    contentTypeSelected(contentType) {
      this.$store.dispatch("selectCommunityContentFilter", { contentType });
      this.loadContent();
    },
    collectionSelected(collection) {
      this.$store.dispatch("selectCommunityContentFilter", { collection });
      this.loadContent();
    },
    onSearch(searchTerm) {
      this.$store.dispatch("selectCommunityContentFilter", { searchTerm });
      this.loadContent();
    },
    async loadContent() {
      await this.$store.dispatch("loadCommunityContent", {
        params: { id: this.$route.params.id },
      });
      this.hasLoadedContent = true;
    },
  },
};
</script>
